import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "react-bootstrap";
import { getRequirementSourcing } from "../../services/requirementsService";
import { errorToast } from "../Toasts/Toasts";

// One requirement, one vendor at a time (MG, 7 Oct 2026).
//
// Each line is costed at the cheapest current rate on its own, so a quote can
// assume a price nobody can actually buy at: RQ-000005's Rs 3,08,025 came
// from three different nurseries for 37,878 plants going to one site. Buying
// the lot from Bunty is Rs 3,26,615 - Rs 18,590, about 6%, for one delivery
// and one person to chase.
//
// This decides nothing. The line costs still carry the cheapest split. It
// puts the premium on screen before the quote goes out rather than after.

const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
const qty = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-IN"));

// The score says how the match was reached, not a probability - so the bands
// are named, not shaded on a gradient.
const scoreBand = (s) => {
  if (s === null || s === undefined) return { cls: "light text-dark", label: "—", title: "Matched before the score existed; re-match to fill it" };
  if (s >= 95) return { cls: "success", label: s, title: "An exact identifier - name, scientific name or size" };
  if (s >= 70) return { cls: "info", label: s, title: "The right plant or size, but more than one record answers to it" };
  if (s >= 40) return { cls: "warning", label: s, title: "A resemblance, or a near size - check before quoting" };
  return { cls: "danger", label: s, title: "Little to go on" };
};

const VendorCoveragePanel = ({ number }) => {
  const [d, setD] = useState(null);
  const [showLines, setShowLines] = useState(false);

  const load = useCallback(async () => {
    try {
      setD(await getRequirementSourcing(number));
    } catch (e) {
      errorToast(e?.message || "Could not read vendor coverage");
    }
  }, [number]);
  useEffect(() => { load(); }, [load]);

  if (!d) return null;
  if (!d.priceableLines) {
    return (
      <div className="card">
        <div className="card-body">
          <h6 className="fw-bold mb-1">Vendor coverage</h6>
          <div className="small text-muted">
            No line has a vendor rate yet, so there is nothing to compare. {d.lineCount ? `${d.lineCount} line(s) are matched; ` : ""}
            ask vendors below, and the coverage appears as rates come in.
          </div>
        </div>
      </div>
    );
  }

  const sv = d.singleVendor;
  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-bold mb-0">Vendor coverage</h6>
          <Button size="sm" variant="link" className="p-0 small" onClick={() => setShowLines(!showLines)}>
            {showLines ? "hide lines" : "line by line"}
          </Button>
        </div>

        {sv && (
          <div className={`alert py-2 small mb-2 ${sv.premium > 0 ? "alert-warning" : "alert-success"}`}>
            {sv.coversEverything ? (
              <>
                <b>{sv.vendorName}</b> can supply all {sv.of} priced line(s) on their own at <b>{inr(sv.goods)}</b>
                {sv.premium > 0
                  ? <> — {inr(sv.premium)} more than the cheapest split across {d.split.vendors.length} vendors ({inr(d.split.goods)}), about {Math.round((sv.premium / (d.split.goods || 1)) * 100)}%.</>
                  : <> — the cheapest on every line, so there is nothing to trade away.</>}
              </>
            ) : (
              <>
                No single vendor covers this requirement. The best, <b>{sv.vendorName}</b>, has {sv.linesCovered} of {sv.of} line(s);
                the rest need somebody else, so the {inr(d.split.goods)} below assumes {d.split.vendors.length} suppliers.
              </>
            )}
          </div>
        )}

        <table className="table table-sm align-middle mb-2">
          <thead>
            <tr>
              <th>Vendor</th>
              <th className="text-end">Lines</th>
              <th className="text-end">Units</th>
              <th className="text-end">Goods</th>
              <th className="text-end">vs cheapest</th>
              <th>Quotes</th>
              <th>Behaviour</th>
            </tr>
          </thead>
          <tbody>
            {d.vendors.map((v) => (
              <tr key={v.vendorId} className={v.coversEverything ? "table-light" : ""}>
                <td>
                  <Link to={`/vendor-inbox?vendor=${v.vendorId}`}>{v.vendorName}</Link>
                  {v.coversEverything && <span className="badge bg-success ms-1">covers all</span>}
                </td>
                <td className="text-end">{v.linesCovered} / {d.priceableLines}</td>
                <td className="text-end">{qty(v.quantity)}</td>
                <td className="text-end">{inr(v.goods)}</td>
                <td className="text-end">
                  {v.premium > 0 ? <span className="text-danger">+{inr(v.premium)}</span> : <span className="text-success">cheapest</span>}
                  <div className="small text-muted">on its {v.linesCovered} line(s)</div>
                </td>
                <td className="small text-muted">
                  {v.newestQuote || "—"}
                  {v.oldestQuote && v.oldestQuote !== v.newestQuote ? <div>oldest {v.oldestQuote}</div> : null}
                </td>
                <td className="small text-muted">{v.behaviour ? <>{v.behaviour.score}/100<div>{v.behaviour.summary}</div></> : "—"}</td>
              </tr>
            ))}
            <tr className="fw-bold">
              <td>Cheapest split <span className="fw-normal text-muted small">— what the line costs assume</span></td>
              <td className="text-end">{d.split.lines} / {d.priceableLines}</td>
              <td className="text-end"></td>
              <td className="text-end">{inr(d.split.goods)}</td>
              <td className="small fw-normal text-muted" colSpan={3}>{d.split.vendors.join(", ")}</td>
            </tr>
          </tbody>
        </table>

        {d.unpriced.length > 0 && (
          <div className="small text-danger mb-2">
            {d.unpriced.length} line(s) no vendor has priced: {d.unpriced.map((l) => `${l.lineNo}. ${l.plantName}${l.height ? ` (${l.height})` : ""}`).join(" · ")}
          </div>
        )}
        {d.separateCharges > 0 && (
          <div className="small text-muted mb-2">
            {d.separateCharges} separate charge(s) — transport, labour — are left out: a nursery does not quote a plant rate for them.
          </div>
        )}

        {showLines && (
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr><th>#</th><th>Line</th><th>Match</th><th className="text-end">Qty</th><th>Every vendor&apos;s current rate</th></tr>
            </thead>
            <tbody>
              {d.lines.map((l) => {
                const band = scoreBand(l.matchScore);
                return (
                  <tr key={l.lineId}>
                    <td>{l.lineNo}</td>
                    <td>
                      {l.plantName}
                      <div className="small text-muted">{l.variantName || <span className="text-warning">{l.matchStatus}</span>}</div>
                    </td>
                    <td><span className={`badge bg-${band.cls}`} title={band.title}>{band.label}</span></td>
                    <td className="text-end">{qty(l.quantity)} {l.unit || ""}</td>
                    <td className="small">
                      {l.options.length
                        ? l.options.map((o, i) => (
                            // already sorted cheapest first; marked so the
                            // choice the line cost made is visible, not inferred
                            <span key={o.rateId} className="me-3">
                              {o.vendorName} <b className={i === 0 ? "text-success" : ""}>{inr(o.rate)}</b>
                              {i === 0 && l.options.length > 1 ? <span className="text-success"> ✓</span> : null}{" "}
                              <span className="text-muted">{o.effectiveDate}{o.confidence && o.confidence !== "High" ? ` · ${o.confidence.toLowerCase()} confidence` : ""}</span>
                            </span>
                          ))
                        : <span className="text-danger">nobody has quoted this</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="small text-muted mt-2">
          Current rate = the newest each vendor has quoted for that exact size, Invalid and Disputed excluded. Never averaged, never borrowed from another size.
        </div>
      </div>
    </div>
  );
};

export default VendorCoveragePanel;
